import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { ClearIcon } from '@mui/x-date-pickers';
import { useSelector } from 'react-redux';
import { ReactComponent as AddIcon } from '../../../../assets/common-assets/Add.svg';
import { Root, classes } from './utils';
import Toast from '../../utils/Toast';
import AgGridTable from './AgGridTable';
import UploadFile from '../../document-management/upload-documents/UploadFile';
import ScanHistoryChart from '../../accountInfo/scan-history/ScanHistoryChart';
import { globalClasses } from '../../utils/globalUtils';
import { SelectOption, VersionSelectOption } from './NestedComponents';
// import { gridFilterSortStatus } from '../../../../services/allInvoiceApi';
import { ACCOUNT_INFO, UPLOAD } from '../../../../constants/staticContent';
import CustomTooltip from '../../utils/CustomTooltip';
import UploadDocument from '../../document-management/upload-documents/UploadDocument';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import CustomFilterModel from './CustomFilterModel';
import {
  getGridFilterSortStatus,
  gridFilterSortStatus,
} from '../../../../services/allInvoiceApi';
import ResetAndApplyButton from '../../document-management/upload-documents/ResetAndApplyButton';

const AgGrid = ({
  className,
  tagNameId,
  functionData,
  showFilterField,
  TitleHeader,
  columnData,
  getRowData,
  DocumentCompletedId,
  DocumentCompletedData,
  DocumentFailedReason,
  gridSettingData,
  gridKey,
  setUploadPending,
  setOpen,
  accuracyDetailsValue,
  setAccuracyDetailsValue = () => {},
  transactionData,
  mapData,
  updateOpenedData,
  selectedVersion,
  changedData,
  versionId,
  isEditing,
  setIsEdited,
  isEdited,
  refreshGrid,
  setOpenAddCredit,
  setGridSettingData,
}) => {
  const location = useLocation();
  const loginRole = getLocalStorageItem('role');
  const uploadStatus = useSelector((state) => state.isUploaded.isUploaded);

  const [isLoading, setIsLoading] = useState(true);

  const [searchValue, setSearchValue] = useState('');

  const [isSearchValueChanged, setIsSearchValueChanged] = useState(false);

  const [isEnterPressed, setIsEnterPressed] = useState(false);

  const [hasData, setHasData] = useState([]);

  const [enterPressed, setEnterPressed] = useState(false);

  const [dataCount, setDataCount] = useState(null);

  const [gridApi, setGridApi] = useState(null);

  const [columnApi, setColumnApi] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);

  const [totalData, setTotalData] = useState([]);

  const [allRowSelected, setAllRowSelected] = useState(false);

  const [customFilterValue, setCustomFilterValue] = useState({});

  const [showTooltip, setShowTooltip] = useState(false);

  const [overAllAccuracy, setOverAllAccuracy] = useState('Not Found');
  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [columnDefs] = useState(columnData);
  const FilteredValue = (event) => {
    setSearchValue(event.target.value);
    if (isEnterPressed && event.target.value !== '') {
      setIsSearchValueChanged(false);
    } else if (isEnterPressed && event.target.value === '') {
      setEnterPressed(!enterPressed);
      setIsEnterPressed(false);
      setIsSearchValueChanged(false);
    }
  };

  const handleSearchValue = async (event) => {
    if (event.key === 'Enter' && searchValue !== '' && !isSearchValueChanged) {
      setEnterPressed(!enterPressed);
      setIsSearchValueChanged(true);
      setIsEnterPressed(true);
    }
  };
  const handleSearchValueOnIconClick = () => {
    if (searchValue !== '') {
      setEnterPressed(!enterPressed);
    }
  };

  const handleClearSearch = () => {
    setEnterPressed(!enterPressed);
    setSearchValue('');
  };

  const getData = async (data) => {
    let res;
    try {
      if (gridKey === 'versions' || gridKey === 'activity-history') {
        res = await getRowData({}, searchValue, {}, data);
      } else if (showFilterField) {
        res = await getRowData(data);
      } else if (gridKey === 'organization-payment-history') {
        res = await getRowData({}, searchValue, {}, data);
      } else if (gridKey !== 'invited-users') {
        res = await getRowData({}, searchValue, {}, {}, data);
      } else if (gridKey === 'Transactions') {
        res = await getRowData();
      }
      if (res) {
        setHasData(res);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      Toast({ message: `${error?.message}`, variant: 'error' });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getData(functionData);
  }, [uploadStatus]);

  const handleCustomFilter = async (
    key,
    sorting,
    filterValue,
    dropDownValues,
  ) => {
    const result = await gridFilterSortStatus(
      key,
      {
        column: sorting?.colId,
        sort: sorting?.sort,
      },
      filterValue,
      dropDownValues,
    );
    if (result) {
      const response = await getGridFilterSortStatus(gridKey);
      setGridSettingData(response);
      gridApi.purgeInfiniteCache();
    }
  };
  useEffect(() => {
    if (gridApi) {
      gridApi.purgeInfiniteCache(); // Clear and reload the data
    }
  }, [refreshGrid]);

  const handleApiCompletion = () => {
    // Refresh the grid when the API action is completed
    gridApi.purgeInfiniteCache();
    gridApi.deselectAll();
    setAllRowSelected(false);
  };

  const handleAllTagCompletion = (updatedTagName) => {
    const nodes = gridApi.getSelectedNodes();
    // const updatedTags = nodes.map((node) => {
    //   const previousTags = node.data.associatedTags || [];
    //   const uniqueTags = [...new Set([...previousTags, ...updatedTagName])];

    //   // Use reduce to filter unique tags without nesting functions deeply
    //   return uniqueTags.reduce((filteredTags, tag) => {
    //     if (filteredTags.findIndex((t) => t.id === tag.id) === -1) {
    //       filteredTags.push(tag);
    //     }
    //     return filteredTags;
    //   }, []);
    // });
    nodes.forEach((node) => {
      const updatedNode = { ...node };
      updatedNode.data.associatedTags = updatedTagName;
      node.updateData({ ...updatedNode.data });
    });
  };

  const navigate = useNavigate();

  const handleResetFilter = async () => {
    const colState = columnApi.getColumnState();
    const filterModels = gridApi.getFilterModel();
    const sortedColumn = colState.find((col) => col.sort !== null);
    const customFilterArray = columnApi?.columnModel?.columnDefs
      .filter((c) => c.filter === 'CustomDropdownFilter')
      .map((m) => m.field);
    const dropdownFilters = customFilterArray?.reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    const customFilterValueSubArrayLength =
      customFilterValue &&
      Object.values(customFilterValue)?.filter((f) => f.length > 0);
    if (
      customFilterValueSubArrayLength &&
      customFilterValueSubArrayLength?.length > 0
    ) {
      setCustomFilterValue(dropdownFilters);
    }
    if (filterModels && Object.keys(filterModels)?.length > 0) {
      gridApi?.setFilterModel({});
    }

    const filterModel = {};
    const dropdownFilter = {};

    await gridFilterSortStatus(
      gridKey,
      {
        column: sortedColumn?.colId,
        sort: sortedColumn?.sort,
      },
      filterModel,
      dropdownFilter,
    );
  };

  const handleRemoveAllFilter = async () => {
    await handleResetFilter();
    const response = await getGridFilterSortStatus(gridKey);
    setGridSettingData(response);
    gridApi.purgeInfiniteCache();
  };
  const handleResetSort = () => {
    const colState = columnApi?.getColumnState();
    const sortedColumn = colState?.find((col) => col.sort !== null);
    if (sortedColumn) {
      columnApi.applyColumnState({
        state: [
          {
            colId: sortedColumn.colId,
            sort: null,
          },
        ],
        applyOrder: true,
      });
    }
  };
  useEffect(() => {
    if (gridSettingData?.filter?.dropDownFilter) {
      setCustomFilterValue(gridSettingData.filter.dropDownFilter);
    }
  }, [gridSettingData?.filter?.dropDownFilter]);
  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      setShowTooltip(windowWidth >= 600 && windowWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (
      location.pathname === '/' &&
      customFilterValue &&
      customFilterValue?.status?.length === 0
    ) {
      navigate('/invoices');
    }
  }, [location.pathname, customFilterValue]);
  const handleApplyFilter = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const renderResetButtons = () => {
    if (isLoading) {
      return null;
    }

    const shouldRenderButtons =
      !(
        customFilterValue?.status?.length === 0 &&
        [
          '/invoices',
          '/receipts',
          '/sales',
          '/bank-statements',
          '/creditcardstatements',
        ].includes(location.pathname) &&
        hasData?.data?.rows?.length === 0
      ) && !showFilterField;
    if (shouldRenderButtons) {
      return (
        <ResetAndApplyButton
          handleApplyFilter={handleApplyFilter}
          handleResetSort={handleResetSort}
        />
      );
    }
    return null;
  };

  const calculateAverageAccuracy = () => {
    const completedDocuments = totalData?.filter(
      (doc) => doc.status === 'Completed' && doc.accuracy !== null,
    );

    if (completedDocuments.length === 0) {
      setOverAllAccuracy('Not Found');
      return;
    }
    const totalAccuracy = completedDocuments.reduce(
      (sum, doc) => sum + parseFloat(doc.accuracy),
      0,
    );
    const averageAccuracy = totalAccuracy / completedDocuments.length;
    const finalOverAllAccuracy = averageAccuracy.toFixed(2).toString();
    setOverAllAccuracy(finalOverAllAccuracy);
  };

  useEffect(() => {
    if (totalData && location.pathname.includes('accuracy-details')) {
      calculateAverageAccuracy();
    }
  }, [totalData]);

  const overallAccuracyText = () => {
    if (isLoading) {
      return null;
    }

    const shouldDropdownforAccuracy =
      !(
        customFilterValue?.status?.length === 0 &&
        location.pathname.includes('accuracy-details') &&
        hasData?.data?.rows?.length === 0
      ) && !showFilterField;

    if (shouldDropdownforAccuracy) {
      return (
        <Typography
          variant="h5"
          component="div"
          className={classes.ExternalHeaderTitle}
        >
          {`Overall Accuracy: ${overAllAccuracy}${overAllAccuracy !== 'Not Found' ? '%' : ''}`}
        </Typography>
      );
    }
    return null;
  };

  const renderDropdownforAccuracy = () => {
    if (isLoading) {
      return null;
    }

    const handleAccuracyDetailsSelectChange = (event) => {
      setAccuracyDetailsValue(event.target.value);
    };

    const shouldDropdownforAccuracy =
      !(
        customFilterValue?.status?.length === 0 &&
        location.pathname.includes('accuracy-details') &&
        hasData?.data?.rows?.length === 0
      ) && !showFilterField;

    if (shouldDropdownforAccuracy) {
      return (
        <Box m={1}>
          <Select
            value={accuracyDetailsValue}
            onChange={handleAccuracyDetailsSelectChange}
            className={classes.accuracySelectionDropdown}
          >
            <MenuItem value="all-documents">All</MenuItem>
            <MenuItem value="Bank Statement">Bank Statement</MenuItem>
            <MenuItem value="Credit Card Statement">
              Credit Card Statement
            </MenuItem>
            <MenuItem value="Sales">Sales</MenuItem>
            <MenuItem value="Direct Expenses">Direct Expenses</MenuItem>
            <MenuItem value="Indirect Expenses">Indirect Expenses</MenuItem>
          </Select>
        </Box>
      );
    }
    return null;
  };

  const renderSearchField = () => {
    if (isLoading) {
      return null;
    }

    const shouldRenderSearchField =
      !(
        customFilterValue?.status?.length === 0 &&
        [
          '/invoices',
          '/receipts',
          '/sales',
          '/bank-statements',
          '/creditcardstatements',
        ].includes(location.pathname) &&
        hasData?.data?.rows?.length === 0
      ) && !showFilterField;

    if (shouldRenderSearchField) {
      return (
        <TextField
          size="small"
          placeholder="Search..."
          autoComplete="off"
          className={classes.SearchField}
          value={searchValue}
          onChange={FilteredValue}
          onKeyDown={handleSearchValue}
          InputProps={{
            endAdornment: (
              <InputAdornment position="start">
                <IconButton
                  onClick={handleClearSearch}
                  className={`${
                    searchValue !== ''
                      ? globalClasses.visibility
                      : globalClasses.notVisibility
                  }`}
                >
                  <ClearIcon />
                </IconButton>
                <IconButton onClick={handleSearchValueOnIconClick}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      );
    }
    return null;
  };

  const handleOpen = () => {
    setOpen(true);
  };
  const handleOpenAddCreditPopupForSales = () => {
    setOpenAddCredit(true);
  };
  const renderContent = () => {
    if (isLoading) {
      return (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      );
    }

    if (showFilterField) {
      return <ScanHistoryChart organizationId={functionData} />;
    }

    const shouldRenderUploadFile =
      customFilterValue?.status?.length === 0 &&
      [
        '/invoices',
        '/receipts',
        '/sales',
        '/bank-statements',
        '/creditcardstatements',
      ].includes(location.pathname) &&
      hasData?.data?.rows?.length === 0;

    if (shouldRenderUploadFile) {
      return (
        <UploadFile
          setEnterPressed={setEnterPressed}
          setUploadPending={setUploadPending}
          documentType={functionData}
        />
      );
    }

    return (
      <Box
        className={
          gridKey === 'Transactions'
            ? classes.gridBoxTransactions
            : classes.gridBox
        }
      >
        <AgGridTable
          className={className}
          gridApi={gridApi}
          setGridApi={setGridApi}
          columnApi={columnApi}
          setColumnApi={setColumnApi}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          setTotalData={setTotalData}
          dataCount={dataCount}
          setDataCount={setDataCount}
          columnData={columnData}
          inputValue={searchValue}
          tagNameId={tagNameId}
          functionData={functionData}
          getRowData={getRowData}
          DocumentCompletedId={DocumentCompletedId}
          DocumentCompletedData={DocumentCompletedData}
          DocumentFailedReason={DocumentFailedReason}
          gridSettingData={gridSettingData}
          enterPressed={enterPressed}
          searchValue={searchValue}
          gridKey={gridKey}
          allRowSelected={allRowSelected}
          setAllRowSelected={setAllRowSelected}
          customFilterValue={customFilterValue}
          setCustomFilterValue={setCustomFilterValue}
          transactionData={transactionData}
          mapData={mapData}
          updateOpenedData={updateOpenedData}
          selectedVersion={selectedVersion}
          changedData={changedData}
          versionId={versionId}
          isEditing={isEditing}
          setIsEdited={setIsEdited}
          isEdited={isEdited}
        />
      </Box>
    );
  };

  return (
    <Root className={classes.root}>
      {!showFilterField &&
        ![
          'invoices',
          'receipts',
          'bank-statements',
          'creditcardstatements',
        ].includes(gridKey) && (
          <VersionSelectOption
            selectedRow={selectedRows}
            allRowSelected={allRowSelected}
            gridKey={gridKey}
            gridApi={gridApi}
            searchValue={searchValue}
            handleApiCompletion={handleApiCompletion}
            rowDataCount={dataCount}
            customFilterValue={customFilterValue}
          />
        )}
      {gridKey !== 'Transactions' && (
        <Box>
          <AppBar
            position="static"
            className={
              gridKey === 'Transactions'
                ? classes.externalHeaderAppBarTransaction
                : classes.externalHeaderAppBar
            }
          >
            <Toolbar variant="dense">
              {showTooltip ? (
                <CustomTooltip title={TitleHeader}>
                  <Typography
                    variant="h5"
                    component="div"
                    className={classes.ExternalHeaderTitle}
                  >
                    {TitleHeader}
                  </Typography>
                </CustomTooltip>
              ) : (
                <Typography
                  variant="h5"
                  component="div"
                  className={classes.ExternalHeaderTitle}
                >
                  {TitleHeader}
                </Typography>
              )}
              {location.pathname.includes('accuracy-details') &&
                overallAccuracyText()}

              {location.pathname.includes('accuracy-details') &&
                renderDropdownforAccuracy()}

              {allRowSelected ? (
                [
                  'invoices',
                  'receipts',
                  'bank-statements',
                  'creditcardstatements',
                ].includes(gridKey) && (
                  <SelectOption
                    totalData={totalData}
                    selectedRow={selectedRows}
                    handleAllTagCompletion={handleAllTagCompletion}
                    gridApi={gridApi}
                    handleApiCompletion={handleApiCompletion}
                    rowDataCount={dataCount}
                    allRowSelected={allRowSelected}
                    searchValue={searchValue}
                    customFilterValue={customFilterValue}
                    functionData={functionData}
                  />
                )
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {renderSearchField()}
                  {renderResetButtons()}

                  {[
                    'invoices',
                    'receipts',
                    'bank-statements',
                    'creditcardstatements',
                  ].includes(gridKey) &&
                  !location.pathname.includes('accuracy-details') ? (
                    <UploadDocument
                      uploadButtonTypo={UPLOAD.UPLOAD}
                      setUploadPending={setUploadPending}
                      documentType={functionData}
                    />
                  ) : null}
                </Box>
              )}

              {(loginRole === ROLES.SUPER_ADMIN ||
                loginRole === ROLES.SUPER_MANAGER ||
                loginRole === ROLES.SALES_ADMIN) &&
              gridKey === 'super-manager' ? (
                <Grid sx={{ display: 'flex' }} gap={1}>
                  <Button
                    variant="contained"
                    className={classes.addOrganizationsButton}
                    startIcon={<AddIcon className={classes.svgIconsSize} />}
                    onClick={handleOpenAddCreditPopupForSales}
                  >
                    Add Credit
                  </Button>
                  <Button
                    variant="contained"
                    className={classes.addOrganizationsButton}
                    startIcon={<AddIcon className={classes.svgIconsSize} />}
                    onClick={handleOpen}
                  >
                    Add To Team
                  </Button>
                </Grid>
              ) : null}

              {gridKey === 'invited-subscriber-detail' ||
              gridKey === 'subscriber-detail' ? (
                <Button
                  variant="contained"
                  className={classes.addOrganizationsButton}
                  startIcon={<AddIcon className={classes.svgIconsSize} />}
                  onClick={handleOpen}
                >
                  Add Organization
                </Button>
              ) : null}

              {gridKey === 'organization-payment-history' &&
              TitleHeader === 'Payment History' &&
              (loginRole === ROLES.SALES_ADMIN ||
                loginRole === ROLES.SALES ||
                loginRole === ROLES.SUPER_ADMIN) ? (
                <Button
                  variant="contained"
                  className={classes.addOrganizationsButton}
                  startIcon={<AddIcon className={classes.svgIconsSize} />}
                  onClick={handleOpen}
                >
                  Add Credit
                </Button>
              ) : null}

              {gridKey === 'users' && loginRole !== ROLES.MEMBER ? (
                <Button
                  variant="contained"
                  className={classes.addMoreButton}
                  startIcon={<AddIcon className={classes.svgIconsSize} />}
                  onClick={handleOpen}
                >
                  {ACCOUNT_INFO.MEMBERS.ADD_MORE}
                </Button>
              ) : null}
            </Toolbar>
            {openMenu && (
              <CustomFilterModel
                open={openMenu}
                anchorEl={anchorEl}
                handleClose={handleClose}
                columnDefs={columnDefs}
                gridKey={gridKey}
                columnApi={columnApi}
                gridSettingData={gridSettingData}
                gridApi={gridApi}
                handleCustomFilter={handleCustomFilter}
                handleResetFilter={handleRemoveAllFilter}
              />
            )}
          </AppBar>
        </Box>
      )}

      {renderContent()}
    </Root>
  );
};

AgGrid.defaultProps = {
  functionData: undefined,
  DocumentCompletedId: undefined,
  DocumentCompletedData: undefined,
  gridSettingData: null,
  tagNameId: undefined,
  showFilterField: undefined,
  DocumentFailedReason: undefined,
  accuracyDetailsValue: null,
  setAccuracyDetailsValue: null,
};

AgGrid.propTypes = {
  tagNameId: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      tag: PropTypes.string.isRequired,
    }),
  ),
  TitleHeader: PropTypes.string.isRequired,
  showFilterField: PropTypes.bool,
  setUploadPending: PropTypes.func.isRequired,
  setOpen: PropTypes.bool.isRequired,
  getRowData: PropTypes.func.isRequired,
  functionData: PropTypes.string,
  columnData: PropTypes.func.isRequired,
  className: PropTypes.string.isRequired,
  DocumentCompletedId: PropTypes.string,
  DocumentCompletedData: PropTypes.string,
  DocumentFailedReason: PropTypes.string,
  gridSettingData: PropTypes.shape({
    id: PropTypes.string,
    user_id: PropTypes.string,
    grid_key: PropTypes.string,
    settings: PropTypes.shape({
      ' ': PropTypes.bool.isRequired,
      name: PropTypes.bool.isRequired,
      status: PropTypes.bool.isRequired,
      associatedTags: PropTypes.bool.isRequired,
      uploaded: PropTypes.bool.isRequired,
      versions: PropTypes.bool.isRequired,
    }),
    sort: PropTypes.shape({
      column: PropTypes.string,
      sort: PropTypes.string,
    }),
    filter: PropTypes.shape({
      dropDownFilter: PropTypes.shape({}),
    }),
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }),
  gridKey: PropTypes.string.isRequired,
  accuracyDetailsValue: PropTypes.string,
  setAccuracyDetailsValue: PropTypes.func,
  transactionData: PropTypes.arrayOf.isRequired,
  mapData: PropTypes.shape({}).isRequired,
  updateOpenedData: PropTypes.func.isRequired,
  selectedVersion: PropTypes.string.isRequired,
  changedData: PropTypes.arrayOf.isRequired,
  versionId: PropTypes.string.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEdited: PropTypes.func.isRequired,
  refreshGrid: PropTypes.bool.isRequired,
  isEdited: PropTypes.bool.isRequired,
  setOpenAddCredit: PropTypes.func.isRequired,
  setGridSettingData: PropTypes.func.isRequired,
};

export default AgGrid;
